import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleIcon, PlayIcon, XMarkIcon, CalendarDaysIcon, InboxIcon, ChevronDownIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { useVendorBookings } from '../../hooks/useVendorBookings'
import { bookingAPI } from '../../services/api/bookings'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { getInputClasses } from '../../styles/inputStyles'
import BookingStatusBadge from './BookingStatusBadge'
import LoadingSpinner from '../LoadingSpinner'
import { toast } from '../../services/toastService'

const ADVANCE_LABEL = {
  pending: 'bookings.accept',
  confirmed: 'bookings.startService',
  in_progress: 'bookings.complete',
}

const ADVANCE_NEXT = {
  pending: 'confirmed',
  confirmed: 'in_progress',
  in_progress: 'completed',
}

const ADVANCE_COLOR = {
  pending: 'bg-primary text-white hover:bg-primary-600',
  confirmed: 'bg-amber-500 text-white hover:bg-amber-600',
  in_progress: 'bg-green-500 text-white hover:bg-green-600',
}

/**
 * VendorBookingQueue - incoming booking requests for a vendor entity.
 * Follows the FavoritesSection / VendorMenuManager list item conventions.
 * @param {string} entityId
 * @idempotent @atomic
 */
const VendorBookingQueue = memo(function VendorBookingQueue({ entityId }) {
  const { t } = useTranslation()
  const { bookings, loading, error, refresh } = useVendorBookings(entityId)
  const { execute } = useAsyncOperation()

  // Track which booking has the inline cancel form open
  const [cancellingId, setCancellingId] = useState(null)
  const [cancelReason, setCancelReason] = useState('')
  const [expandedId, setExpandedId] = useState(null)

  const toggleExpand = useCallback((groupId) => {
    setExpandedId(prev => prev === groupId ? null : groupId)
  }, [])

  const advance = useCallback(async (groupId, currentStatus) => {
    const next = ADVANCE_NEXT[currentStatus]
    if (!next) return
    await execute(() => bookingAPI.updateGroupStatus(groupId, next, ''), {
      onSuccess: () => {
        toast.success(t(`bookings.advancedTo.${next}`, `Marked as ${next}`))
        refresh()
      },
      onError: () => toast.error(t('bookings.updateFailed', 'Failed to update booking')),
    })
  }, [execute, t, refresh])

  const openCancel = useCallback((groupId) => {
    setCancellingId(groupId)
    setCancelReason('')
  }, [])

  const submitCancel = useCallback(async (groupId) => {
    if (!cancelReason.trim()) {
      toast.error(t('bookings.cancellationReasonRequired', 'Please provide a reason'))
      return
    }
    await execute(() => bookingAPI.updateGroupStatus(groupId, 'cancelled', cancelReason.trim()), {
      onSuccess: () => {
        toast.success(t('bookings.cancelled', 'Booking cancelled'))
        setCancellingId(null)
        setCancelReason('')
        refresh()
      },
      onError: () => toast.error(t('bookings.updateFailed', 'Failed to cancel booking')),
    })
  }, [cancelReason, execute, t, refresh])

  if (loading) return <LoadingSpinner size="sm" />
  if (error) return <p className="text-sm text-red-500 py-3 text-center">{t('common.error', 'Failed to load')}</p>
  if (!bookings.length) return (
    <div className="p-6 text-center">
      <InboxIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
      <p className="text-sm text-gray-500 font-medium">{t('bookings.noVendorBookings', 'No booking requests yet')}</p>
      <p className="text-xs text-gray-400 mt-1">{t('bookings.noVendorBookingsHint', 'When customers book your services, requests will appear here.')}</p>
    </div>
  )

  const pending = bookings.filter(b => b.status === 'pending').length
  const active  = bookings.filter(b => b.status === 'confirmed' || b.status === 'in_progress').length

  return (
    <div className="space-y-2 p-2">
      {/* Summary bar */}
      {(pending > 0 || active > 0) && (
        <div className="flex gap-2 px-1 pb-1">
          {pending > 0 && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
              {pending} {t('bookings.pendingCount', 'pending')}
            </span>
          )}
          {active > 0 && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-primary bg-primary/5 border border-primary/20 rounded-full px-2 py-0.5">
              {active} {t('bookings.activeCount', 'active')}
            </span>
          )}
        </div>
      )}
      {bookings.map((b) => {
        const isActive = b.status !== 'completed' && b.status !== 'cancelled'
        const isCancelling = cancellingId === b.group_id
        const isExpanded = expandedId === b.group_id
        const contact = b.first_booking?.address
        const schedulingType = b.first_booking?.scheduling_type
        const itemNotes = b.first_booking?.notes

        return (
          <div
            key={b.group_id}
            className="bg-white rounded-md border border-gray-200/60 hover:border-gray-300 hover:shadow-sm transition-all"
          >
            {/* Row header */}
            <div className="flex items-start gap-2.5 p-3">
              <CalendarDaysIcon className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 break-words">{b.customer_name}</p>
                {b.first_booking?.offering_name && (
                  <p className="text-xs text-gray-600 mt-0.5">{b.first_booking.offering_name}</p>
                )}
                {b.first_booking?.scheduled_date && (
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    {b.first_booking.scheduled_date}
                    {b.first_booking.scheduled_time && ` · ${b.first_booking.scheduled_time}`}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(b.created_at).toLocaleDateString()}
                </p>
              </div>
              <BookingStatusBadge status={b.status} />
            </div>

            {/* Expand/collapse toggle */}
            <button
              onClick={() => toggleExpand(b.group_id)}
              className="w-full flex items-center justify-between px-3 py-1.5 text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors border-t border-gray-100"
            >
              <span>{isExpanded ? t('common.hideDetails', 'Hide details') : t('common.showDetails', 'Show details')}</span>
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>

            {/* Expanded detail panel — customer contact + scheduling info */}
            {isExpanded && (
              <div className="px-3 pb-3 pt-1 space-y-2 border-t border-gray-100 bg-gray-50/60">
                {contact ? (
                  <div className="flex items-start gap-2">
                    <span className="text-[11px] font-semibold text-gray-500 flex-shrink-0 mt-0.5">{t('bookings.customerContact', 'Contact')}</span>
                    {contact.includes('@') ? (
                      <a href={`mailto:${contact}`} className="flex items-center gap-1 text-xs text-primary hover:underline break-all">
                        <EnvelopeIcon className="w-3.5 h-3.5 flex-shrink-0" />{contact}
                      </a>
                    ) : (
                      <a href={`tel:${contact}`} className="flex items-center gap-1 text-xs text-primary hover:underline">
                        <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />{contact}
                      </a>
                    )}
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-400 italic">{t('bookings.noContactHint', 'No contact info provided — reach out through the entity channel.')}</p>
                )}
                {schedulingType === 'flexible' && (
                  <p className="text-[11px] text-gray-500">{t('bookings.flexibleScheduling', 'Flexible schedule — confirm a time with the customer.')}</p>
                )}
                {itemNotes && (
                  <div>
                    <span className="text-[11px] font-semibold text-gray-500">{t('bookings.itemNotes', 'Customer notes')}</span>
                    <p className="text-xs text-gray-600 mt-0.5 italic">&ldquo;{itemNotes}&rdquo;</p>
                  </div>
                )}
                {b.booking_count > 1 && (
                  <p className="text-[11px] text-gray-400">{t('bookings.multipleItems', '{{count}} service items in this request', { count: b.booking_count })}</p>
                )}
                {b.notes && (
                  <div>
                    <span className="text-[11px] font-semibold text-gray-500">{t('bookings.groupNotes', 'Request notes')}</span>
                    <p className="text-xs text-gray-600 mt-0.5 italic">&ldquo;{b.notes}&rdquo;</p>
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            {isActive && !isCancelling && (
              <div className="flex gap-2 px-3 pb-3">
                {ADVANCE_NEXT[b.status] && (
                  <button
                    onClick={() => advance(b.group_id, b.status)}
                    onTouchEnd={(e) => { e.preventDefault(); advance(b.group_id, b.status) }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${ADVANCE_COLOR[b.status] || 'bg-primary text-white'}`}
                  >
                    {b.status === 'pending' && <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />}
                    {b.status === 'confirmed' && <PlayIcon className="w-3.5 h-3.5 flex-shrink-0" />}
                    {b.status === 'in_progress' && <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />}
                    {t(ADVANCE_LABEL[b.status], ADVANCE_NEXT[b.status])}
                  </button>
                )}
                <button
                  onClick={() => openCancel(b.group_id)}
                  onTouchEnd={(e) => { e.preventDefault(); openCancel(b.group_id) }}
                  className="flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
                >
                  <XMarkIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            )}

            {/* Inline cancellation form */}
            {isCancelling && (
              <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
                <p className="text-xs font-medium text-gray-700">{t('bookings.cancellationReasonPrompt', 'Reason for cancellation (required)')}</p>
                <input
                  type="text"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder={t('bookings.cancellationReasonPrompt', 'Reason...')}
                  className={getInputClasses(false)}
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => submitCancel(b.group_id)}
                    onTouchEnd={(e) => { e.preventDefault(); submitCancel(b.group_id) }}
                    disabled={!cancelReason.trim()}
                    className="flex-1 py-1.5 px-3 bg-red-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition-opacity hover:bg-red-600"
                  >
                    {t('bookings.cancel', 'Cancel booking')}
                  </button>
                  <button
                    onClick={() => setCancellingId(null)}
                    onTouchEnd={(e) => { e.preventDefault(); setCancellingId(null) }}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 rounded-lg border border-gray-200 transition-colors"
                  >
                    {t('common.back', 'Back')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
})

export default VendorBookingQueue
